import { Button, Icon, Modal, TextInput, TextStatic } from '@abb/common-ux';
import { IUserToken } from '@module/authentication/models';
import { IRequestReportDetails } from '@module/shared';
import { DropdownTreeSelect } from '@module/shared/components/DropdownTreeSelect';
import { IDropdownTreeSelectOption } from '@module/shared/components/DropdownTreeSelect/DropdownTreeSelect';
import { Waiting } from '@module/shared/components/Waiting';
import { IBusinessLineHub } from '@module/shared/models/SalesLeadModels';
import { SalesLeadService } from '@service/SalesLeadService';
import * as React from 'react';
import './SubmitSalesLead.scss';

interface ISubmitSalesLeadProps {
  user: IUserToken;
  dispatch: any;
  requestDetails: IRequestReportDetails;
  updateLeadGeneratedInRequestDetails: () => void;
}

interface ISubmitSalesLeadState {
  waiting: boolean;
  businessLineHubs?: IBusinessLineHub[];
  options?: IDropdownTreeSelectOption[];
  isOpen: boolean;
  selectedQueue?: IDropdownTreeSelectOption;
  campaign: string;
  opportunityDescription: string;
  suggestedSolution: string;
  urgencyOfRequest: string;
  opportunityValue?: number;
  leadGeneration: boolean;
}

export class SubmitSalesLead extends React.Component<ISubmitSalesLeadProps, ISubmitSalesLeadState> {

  constructor(props: ISubmitSalesLeadProps) {
    super(props);

    this.state = {
      waiting: false,
      isOpen: false,
      campaign: '',
      opportunityDescription: '',
      suggestedSolution: '',
      urgencyOfRequest: '',
      leadGeneration: false
    };
  }

  async componentDidUpdate() {
    await this.getBusinessLineHubs();
  }

  getBusinessLineHubs = async () => {
    if (this.props.requestDetails.associatedSite && this.props.requestDetails.statusName === 'Approved' && this.state.businessLineHubs === undefined) {
      try {
        const businessLineHubs = await SalesLeadService.getBusinessLineHubs(this.props.user);
        const nodes: IDropdownTreeSelectOption[] = [{id: 'root', nodeText: 'n/a', parentID: '', value: '', display: ''}];
        businessLineHubs.forEach(i => {
          if (i.businessLine && !nodes.some(n => n.id === i.businessLine)) {
              nodes.push({
                  id: i.businessLine,
                  parentID: 'root',
                  nodeText: i.businessLine,
                  value: '',
                  display: ''
              });
          }
          if (i.hub && !nodes.some(n => n.id === `${i.businessLine}-${i.hub}`)) {
            nodes.push({
              id: i.country && i.country === 'HUB' ? i.id.toString() : `${i.businessLine}-${i.hub}`,
              parentID: i.businessLine,
              nodeText: i.hub,
              value: i.country && i.country === 'HUB' ? i.hub : '',
              display: i.country && i.country === 'HUB' ? `${i.businessLine} - ${i.hub}` : ''
          });
          }
          if (i.country && i.country !== 'HUB') {
            nodes.push({
              id: i.id.toString(),
              parentID: `${i.businessLine}-${i.hub}`,
              nodeText: i.country,
              value: i.country,
              display: `${i.businessLine} - ${i.hub} - ${i.country}`
            });
          }
        });
        this.setState({businessLineHubs, options: nodes});
      } catch (error) {
        const link = <a style={{ color: '#0057ff' }} href={`mailto:us-iasvsupport@abb.com?Subject=mySpareParts%20Analyzer,%20Request%20Summary%20Details%20Error`} target='_top'>us-iasvsupport@abb.com</a>;
        const message = <span>Something went wrong.  Please try again or contact Customer Support at {link}.</span>;
        this.showBanner('error', message);
      }
    }
  }

  render() {
    const submitSalesLeadBtnDisabled = !this.props.requestDetails.associatedSite || this.props.requestDetails.statusName !== 'Approved' || this.props.requestDetails.leadGenerated;
    const SendDisabled = this.state.campaign === '' || this.state.selectedQueue === undefined || this.state.opportunityDescription === '';
    const displayedContent = (this.props.requestDetails.leadGenerated || this.state.leadGeneration
      ? (
          <span className='iconStyling'><Icon icon='check_mark_circle2' size='small' color='green'/>{'  '}Lead generated</span>
        )
      : <Button className='action-btn' buttonType='primary' buttonSize='default' disabled={submitSalesLeadBtnDisabled} onClick={this.openModal}> Submit a Sales Lead</Button>
    );
    return (
      <>
        {displayedContent}
        <Waiting isOpen={this.state.waiting} />
        <Modal isOpen={this.state.isOpen} header={'Submit a Sales Lead'} onClose={this.handleCancel} modalClassName='submit-sales-lead-modal'>
          <form id='SalesLeadForm' onSubmit={this.handleSend}>
            <div className='table'>
              <div className='row'>
                <div className='col-xs-2'>Parts Specialist:</div>
                <div className='col-xs-10 boldText' style={{paddingLeft: '47px'}}>{this.props.requestDetails.partsSpecialistName}</div>
              </div>
              <br/>
              <div className='row'>
                <div className='col-xs-6'>
                  <div className='row'>
                    <div className='col-xs-5'>Company Name:</div>
                    <div className='col-xs-7 boldText'>{this.props.requestDetails.companyName}</div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-5'>Company Location:</div>
                    <div className='col-xs-7 boldText'>{this.props.requestDetails.location}</div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-5'>ServIS Site Reference:</div>
                    <div className='col-xs-7 boldText'>{this.props.requestDetails.associatedSite ? this.props.requestDetails.associatedSite.servIsId : ''}</div>
                  </div>
                </div>
                <div className='col-xs-6'>
                  <div className='row'>
                    <div className='col-xs-5'>Customer Name:</div>
                    <div className='col-xs-7 boldText'>{this.props.requestDetails.contactName}</div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-5'>Customer Email:</div>
                    <div className='col-xs-7 boldText'>{this.props.requestDetails.contactEmail}</div>
                  </div>
                  <div className='row'>
                    <div className='col-xs-5'>Customer Phone:</div>
                    <div className='col-xs-7 boldText'>{this.props.requestDetails.phoneNumber}</div>
                  </div>
                </div>
              </div>
              <br/>
              <div className='row'>
                <div className='col-xs-12'>
                  <TextInput 
                    name='campaign' inputSize='small' 
                    labelText='Campaign:' isRequired={true} placeholder={'type...'}
                    onChange={this.handleInputChange} 
                    value={this.state.campaign} />
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-12'>
                  <DropdownTreeSelect 
                    name='queue'
                    labelText='Select a Salesforce.com queue:' 
                    isRequired={true} placeholder={'+ Division + HUB + Country'}
                    selected={this.state.selectedQueue}
                    options={this.state.options === undefined ? [] : this.state.options}
                    onChange={this.handleQueueChange}
                   />
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-12'>
                  <TextInput 
                    name='opportunityDescription' inputSize='small'
                    labelText='Opportunity Description:' isRequired={true} isTextArea={true}
                    onChange={this.handleInputChange} placeholder={'type...'}
                    value={this.state.opportunityDescription} />
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-12'>
                  <TextInput 
                    name='suggestedSolution' inputSize='small' 
                    labelText='Brief Description of Suggested Solution or Service to be provided:' isTextArea={true}
                    onChange={this.handleInputChange} placeholder={'type...'}
                    value={this.state.suggestedSolution} />
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-12'>
                  <TextInput 
                    name='urgencyOfRequest' inputSize='small' 
                    labelText='How urgent is the request from the customer’s perspective and why?  (Ex:  When would the customer need this?):' isTextArea={true}
                    onChange={this.handleInputChange} placeholder={'type...'}
                    value={this.state.urgencyOfRequest} />
                </div>
              </div>
              <div className='row'>
                <div className='col-xs-4'>Expected value of opportunity:</div>
                <div className='col-xs-6'>
                  <TextInput 
                    name='opportunityValue' inputSize='small' pattern='[0-9]*'
                    onChange={this.handleInputChange} placeholder={'value...'}
                    value={this.state.opportunityValue !== undefined ? this.state.opportunityValue.toString() : ''} />
                </div>
                <div className='col-xs-2 boldText'><TextStatic TextValue={'USD'}/></div>
              </div>
            </div>
            <div className='text-right action-reg'>
              <Button type='button' buttonType='discrete' buttonSize='default' onClick={this.handleCancel}>Cancel</Button>
              <Button type='submit' buttonType='primary' buttonSize='default' disabled={SendDisabled}>Send</Button>
            </div>
          </form>
        </Modal>
      </>
    );
  }

  openModal = () => {
    this.setState({
      isOpen: true,
      campaign: '',
      opportunityDescription: '',
      suggestedSolution: '',
      urgencyOfRequest: '',
      opportunityValue: undefined,
      selectedQueue: undefined
    });
  }

  handleCancel = () => {
    this.setState({isOpen: false});
  }

  handleSend = async () => {
    const data = {
      sparePartsUploadDetailsID: this.props.requestDetails.sparePartsUploadDetailsID,
      campaign: this.state.campaign,
      businessLineHubID: this.state.selectedQueue!.id,
      opportunityDescription: this.state.opportunityDescription,
      suggestedSolution: this.state.suggestedSolution === '' ? null : this.state.suggestedSolution,
      urgencyOfRequest: this.state.urgencyOfRequest === '' ? null : this.state.urgencyOfRequest,
      opportunityValue: this.state.opportunityValue === undefined ? 0 : this.state.opportunityValue
    };
    this.setState({waiting: true, isOpen: false, leadGeneration: true});
    try {
      await SalesLeadService.submitSalesLead(this.props.user, data);
      this.props.updateLeadGeneratedInRequestDetails();
      this.showBanner('success', <span>Submitted Sales Lead successfully.</span>, 3000);
    } catch (error) {
      const link = <a style={{ color: '#0057ff' }} href={`mailto:us-iasvsupport@abb.com?Subject=mySpareParts%20Analyzer,%20Request%20Summary%20Details%20Error`} target='_top'>us-iasvsupport@abb.com</a>;
      const message = <span>Something went wrong.  Please try again or contact Customer Support at {link}.</span>;
      this.showBanner('error', message);
    }
    this.setState({waiting: false, leadGeneration: false});
  }

  handleInputChange = (e: React.ChangeEvent<any>) => {
    const value = e.target.value;
    const name = e.target.name;
    if (name === 'opportunityValue') {
      const opportunityValue = parseInt(value.length < 10 ? value : value.substr(0, 9), 10); 
      this.setState({opportunityValue: Number.isNaN(opportunityValue) ? undefined : opportunityValue});
    } else if (!value.startsWith(' ')) {
      this.setState({ ...this.state, [`${name}`]: value });
    }
  }

  handleQueueChange = (selected?: IDropdownTreeSelectOption) => {
    this.setState({selectedQueue: selected});
  }

  showBanner = (typ?: string, message?: JSX.Element, time?: number) => {
    this.props.dispatch({ type: 'ADD_BANNER', banners: [{ bannerType: typ, timeout: time, children: message }] });
  }
}
